/**
 * LocationDetectedBanner — first-visit only.
 *
 * Behaviour:
 *   - Renders only when `keikka_prefs_v1` is not yet set.
 *   - Additionally suppressed for 30 days after user dismisses ("No") via
 *     `keikka_geo_dismissed_at` (epoch ms).
 *   - Fetches `/api/geo/detect`; if `country_code` is returned, proposes it.
 *   - Buttons:
 *       Yes    — saves detected country into prefs (default map / work / service)
 *       Change — opens the Preferences sheet via `window` event
 *       No     — stamps dismissal, hides banner for 30d
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/context';
import { usePrefs, PREFS_STORAGE_KEY } from '@/lib/usePrefs';

// Map country code → UI language that best matches the market. Used on
// "Accept" so a Norwegian visitor gets both the NO market AND the Norwegian
// UI in one click.
const COUNTRY_TO_UI_LANG = {
  NO: 'nb',
  SE: 'sv',
  FI: 'fi',
  EE: 'et',
  RU: 'ru',
  LV: 'et',
  LT: 'et',
};

const DISMISS_KEY = 'keikka_geo_dismissed_at';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const API = process.env.REACT_APP_BACKEND_URL;

function isRecentlyDismissed() {
  try {
    const v = window.localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = Number(v);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch (_) {
    return false;
  }
}

function hasAnyPrefs() {
  try {
    const raw = window.localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw);
    return !!(p && (p.workCountry || p.serviceCountry || p.uiLang || p.locationQuery));
  } catch (_) {
    return false;
  }
}

export const LocationDetectedBanner = () => {
  const { t, setLanguage } = useLanguage();
  const { updatePrefs } = usePrefs();
  const [detected, setDetected] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // First visit gate: prefs unset AND not recently dismissed
    if (hasAnyPrefs() || isRecentlyDismissed()) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/geo/detect`, { timeout: 3500 });
        const data = res?.data || {};
        if (cancelled) return;
        if (data.country_code) {
          setDetected({
            code: data.country_code,
            name: data.country_name || data.country_code,
          });
          setVisible(true);
        }
      } catch (_) {
        // silent: no banner if detection fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible || !detected) return null;

  const accept = () => {
    const suggestedLang = COUNTRY_TO_UI_LANG[detected.code];
    updatePrefs({
      workCountry: detected.code,
      serviceCountry: detected.code,
      ...(suggestedLang ? { uiLang: suggestedLang } : {}),
    });
    if (suggestedLang) {
      try { setLanguage(suggestedLang); } catch (_) {}
    }
    setVisible(false);
  };

  const openSettings = () => {
    // PreferencesSheet listens for this event and opens itself.
    window.dispatchEvent(new CustomEvent('keikka:open-prefs'));
    setVisible(false);
  };

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setVisible(false);
  };

  const label = t('homeV3.geoBannerText').replaceAll('{country}', detected.name);

  return (
    <div
      className="max-w-[1200px] mx-auto px-4 pt-3"
      data-testid="geo-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <MapPin className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-slate-800 leading-snug">{label}</p>
        </div>
        <div className="flex items-center gap-2 justify-end flex-shrink-0">
          <Button
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 text-white"
            onClick={accept}
            data-testid="geo-banner-yes"
          >
            {t('homeV3.geoBannerYes')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-slate-300"
            onClick={openSettings}
            data-testid="geo-banner-change"
          >
            {t('homeV3.geoBannerChange')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-slate-500 hover:text-slate-700"
            onClick={dismiss}
            data-testid="geo-banner-no"
          >
            {t('homeV3.geoBannerNo')}
          </Button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close banner"
            data-testid="geo-banner-close"
            className="p-1 rounded hover:bg-orange-100 text-slate-400 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationDetectedBanner;
